import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Application } from '@feathersjs/feathers';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Title } from '../../components';
import TabularReport from './TabularReport';
import TemporalReport from './TemporalReport';
import { AnalyticReport, CustomQueryFieldDefinition } from '../../types/analytic-reports/AnalyticReport';

const Reports: React.FC = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [reportsCatalog, setReportsCatalog] = useState<AnalyticReport[]>([]);
  const [selectedReportKey, setSelectedReportKey] = useState<string>('');
  const [report, setReport] = useState<AnalyticReport | null>(null);
  const [queryParams, setQueryParams] = useState<Record<string, unknown>>({});
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async (reportKey: string, params: Record<string, unknown>) => {
    if (!feathers) return;
    setLoadingReport(true);
    setError(null);

    try {
      const response = await feathers
        .getService('analytic-reports')
        .get(reportKey, { query: params });
      setReport(response);
      const returnedQueryParams = response?.result?.queryParams || {};
      const queryParamsWithDefaults = (response.customQueryFields || []).reduce<Record<string, unknown>>(
        (acc, field) => {
          if (acc[field.name] === undefined && field.default !== undefined) {
            acc[field.name] = field.default;
          }
          return acc;
        },
        { ...returnedQueryParams }
      );
      setQueryParams(queryParamsWithDefaults);
    } catch (err) {
      console.error(`Failed to load report "${reportKey}"`, err);
      setError(`Could not load report "${reportKey}".`);
    } finally {
      setLoadingReport(false);
    }
  }, [feathers]);

  useEffect(() => {
    const loadCatalog = async () => {
      if (!feathers) return;
      setLoadingCatalog(true);
      setError(null);

      try {
        const listResponse = await feathers.getService('analytic-reports').find({});
        const reports = listResponse?.data || [];
        setReportsCatalog(reports);

        const defaultReport = reports.find((entry: AnalyticReport) => entry.key === 'organization-costing-yearly') || reports[0];
        if (defaultReport?.key) {
          setSelectedReportKey(defaultReport.key);
          await loadReport(defaultReport.key, {});
        } else {
          setReport(null);
        }
      } catch (err) {
        console.error('Failed to load reports catalog', err);
        setError('Could not load reports catalog.');
      } finally {
        setLoadingCatalog(false);
      }
    };

    loadCatalog();
  }, [feathers, loadReport]);

  const handleCustomQueryFieldChange = useCallback((name: string, value: unknown) => {
    setQueryParams((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const getInputType = (fieldType: CustomQueryFieldDefinition['fieldType']) => {
    if (fieldType === 'date') return 'date';
    if (fieldType === 'number' || fieldType === 'currency' || fieldType === 'percentage') return 'number';
    return 'text';
  };

  const toCustomFieldValue = (field: CustomQueryFieldDefinition, value: string): unknown => {
    if (value === '') {
      return undefined;
    }
    if (field.fieldType === 'number' || field.fieldType === 'currency' || field.fieldType === 'percentage') {
      return Number(value);
    }
    return value;
  };

  const handleCustomFieldChange = (field: CustomQueryFieldDefinition, value: string) => {
    handleCustomQueryFieldChange(field.name, toCustomFieldValue(field, value));
  };

  const handleCustomFieldBlur = (field: CustomQueryFieldDefinition, value: string) => {
    if (!selectedReportKey) return;
    const nextQueryParams = {
      ...queryParams,
      [field.name]: toCustomFieldValue(field, value)
    };
    loadReport(selectedReportKey, nextQueryParams);
  };

  const handleTemporalDateRangeChange = useCallback(async (startDate: string, endDate: string) => {
    if (!selectedReportKey) return;
    const nextQueryParams = {
      ...queryParams,
      startDate,
      endDate
    };
    setQueryParams(nextQueryParams);
    await loadReport(selectedReportKey, nextQueryParams);
  }, [selectedReportKey, queryParams, loadReport]);

  const rows = report?.result?.data || [];
  const customQueryFields = report?.customQueryFields || [];
  const loading = loadingCatalog || loadingReport;

  const handleReportChange = async (event: SelectChangeEvent<string>) => {
    const nextReportKey = event.target.value;
    setSelectedReportKey(nextReportKey);
    setQueryParams({});
    await loadReport(nextReportKey, {});
  };

  const renderedReport = useMemo(() => {
    if (!report) return null;
    if (report.type === 'temporal') {
      return (
        <TemporalReport
          report={report}
          onDateRangeChange={handleTemporalDateRangeChange}
          loading={loadingReport}
        />
      );
    }
    return <TabularReport report={report} />;
  }, [report, handleTemporalDateRangeChange, loadingReport]);

  if ((!report && loading) || loadingCatalog) {
    return (
      <Box sx={{ p: 3 }}>
        <Title>Reports</Title>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading reports...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Title>{report?.title || 'Reports'}</Title>

      {!loading && error && (
        <Alert severity="error">{error}</Alert>
      )}

      {!loading && !error && report && rows.length === 0 && (
        <Alert severity="info">No report data found.</Alert>
      )}

      {!loading && !error && report && (
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h3">{report.title}</Typography>
            {
              report.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {report.description}
                </Typography>
              )
            }
          </Box>
          <Stack direction="row" spacing={1} alignItems="flex-end" justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="report-select-label">Report</InputLabel>
              <Select
                labelId="report-select-label"
                id="report-select"
                value={selectedReportKey}
                label="Report"
                onChange={handleReportChange}
              >
                {reportsCatalog.map((entry) => (
                  <MenuItem key={entry.key} value={entry.key}>
                    {entry.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {customQueryFields.map((field) => (
              <TextField
                key={field.name}
                size="small"
                label={field.label}
                type={getInputType(field.fieldType)}
                value={String(queryParams[field.name] ?? field.default ?? '')}
                required={!field.optional}
                onChange={(event) => handleCustomFieldChange(field, event.target.value)}
                onBlur={(event) => handleCustomFieldBlur(field, event.target.value)}
                InputLabelProps={field.fieldType === 'date' ? { shrink: true } : undefined}
                sx={{
                  width: 120,
                  '& .MuiInputBase-root': {
                    height: 30,
                    fontSize: '0.75rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.72rem'
                  }
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {!loading && !error && report && (
        renderedReport
      )}
    </Box>
  );
};

export default Reports;
